import { AlertTriangle, Clock, XCircle } from 'lucide-react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { WorkOrderType } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useMayManageCampaign } from '@/hooks';
import { useCreatePortal, useBillingStatus, useWorkOrder } from '@/hooks/queries';
import { voterDataPathKind } from '@/lib/paywallExemptRoutes';
import { campaignDaysUntil } from '@/lib/calendarUtils';

/**
 * Voter data is bought without a subscription (ADR-090), so a "pay to gain access" banner there
 * would say the opposite. A work order's page stays quiet until its type is known.
 */
function useOnVoterData(pathname: string): boolean {
  const place = voterDataPathKind(pathname);
  const { data: order } = useWorkOrder(place.kind === 'workOrder' ? place.id : undefined);
  if (place.kind === 'builder') return true;
  if (place.kind === 'workOrder') return order?.type !== WorkOrderType.TextSend;
  return false;
}

const ORDERS_PATH = /^\/app\/work-orders(\/|$)/;

export function BillingBanner() {
  const { pathname } = useLocation();
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const { mutate: createPortal, isPending } = useCreatePortal();
  const { data: billingStatus } = useBillingStatus();
  const navigate = useNavigate();
  const onVoterData = useOnVoterData(pathname);
  const mayManageBilling = useMayManageCampaign();

  const status = billingStatus?.subscriptionStatus ?? selectedCampaign?.subscriptionStatus;
  const trialEndsAt = billingStatus?.trialEndsAt ?? selectedCampaign?.trialEndsAt;

  if (onVoterData) return null;

  // A campaign that came through an Express link to send a text paid for what it came for, so its order
  // pages don't sell it the subscription; quiet until billing status says which campaign this is.
  const sellsSubscription = !ORDERS_PATH.test(pathname) || billingStatus?.expressTextSend === false;
  if ((status === 'Trialing' || status === 'Unpaid') && !sellsSubscription) return null;

  if (status === 'Trialing') {
    if (!trialEndsAt) return null;
    const days = campaignDaysUntil(trialEndsAt, selectedCampaign?.timeZoneId);

    const message =
      days > 0
        ? `${days} day${days === 1 ? '' : 's'} left in your free trial.`
        : mayManageBilling
          ? 'Your free trial expires today. Subscribe to keep access.'
          : "This campaign's free trial expires today. Ask your campaign owner to subscribe to keep access.";

    return (
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-blue-800">
          <Clock className="h-4 w-4" />
          <span>{message}</span>
        </div>
        {mayManageBilling && (
          <button
            onClick={() => navigate({ to: '/app/settings/billing' })}
            className="text-sm font-medium text-blue-800 underline hover:text-blue-900"
          >
            Subscribe now
          </button>
        )}
      </div>
    );
  }

  if (status === 'Unpaid') {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-red-800">
          <XCircle className="h-4 w-4" />
          <span>
            {mayManageBilling
              ? 'Make payment to gain access.'
              : "This campaign's subscription is unpaid. Ask your campaign owner to make a payment to restore access."}
          </span>
        </div>
        {mayManageBilling && (
          <button
            onClick={() => navigate({ to: '/app/settings/billing' })}
            className="text-sm font-medium text-red-800 underline hover:text-red-900"
          >
            Subscribe now
          </button>
        )}
      </div>
    );
  }

  if (status === 'PastDue') {
    return (
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4" />
          <span>
            {mayManageBilling
              ? 'Payment failed. Please update your payment method.'
              : "This campaign's last payment failed. Ask your campaign owner to update the payment method."}
          </span>
        </div>
        {mayManageBilling && (
          <button
            onClick={() => createPortal({ returnUrl: window.location.href })}
            disabled={isPending}
            className="text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
          >
            {isPending ? 'Loading...' : 'Update payment method'}
          </button>
        )}
      </div>
    );
  }

  return null;
}
